import { Appointment, AppointmentFormData } from '../components/Calendar/types';
import { getAuthToken } from './authStorage';
import { API_BASE_URL } from './apiBaseUrl';

// Interface pour les réponses API
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    message: string;
    details?: any;
  };
}

const extractApiErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as ApiResponse<unknown>;
    return payload.error?.message || payload.message || `Erreur HTTP: ${response.status}`;
  } catch {
    return `Erreur HTTP: ${response.status}`;
  }
};

const buildAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Interface pour les filtres de recherche
interface ScheduleFilters {
  startDate?: string;
  endDate?: string;
  clientId?: string;
  status?: string;
  view?: 'day' | 'week' | 'month';
}

// Interface pour les statistiques
interface DayStatistics {
  totalTravelTime: number;
  totalDistance: number;
  numberOfClients: number;
  efficiencyScore: number;
  geographicalSpread: number;
}

// Interface pour les créneaux disponibles
interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  duration: number;
  isAvailable: boolean;
  reason?: string;
}

const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export class ScheduleApiService {
  /**
   * Récupérer l'agenda avec filtres
   */
  static async getSchedule(filters: ScheduleFilters = {}): Promise<{
    appointments: Appointment[];
    view: string;
    period: any;
    totalCount: number;
  }> {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `${API_BASE_URL}/schedule?${queryString}`
      : `${API_BASE_URL}/schedule`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la récupération de l\'agenda');
    }

    return result.data;
  }

  /**
   * Récupérer l'agenda d'une journée
   */
  static async getDaySchedule(date: Date): Promise<{
    date: string;
    appointments: Appointment[];
    availableSlots: TimeSlot[];
    totalDuration: number;
    clientCount: number;
  }> {
    const dateStr = toLocalDateString(date);
    
    const response = await fetch(`${API_BASE_URL}/schedule/day/${dateStr}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la récupération de l\'agenda du jour');
    }

    return result.data;
  }

  /**
   * Récupérer l'agenda d'une semaine
   */
  static async getWeekSchedule(date: Date): Promise<{
    weekStart: string;
    weekEnd: string;
    appointments: Appointment[];
  }> {
    const dateStr = toLocalDateString(date);
    
    const response = await fetch(`${API_BASE_URL}/schedule/week/${dateStr}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la récupération de l\'agenda de la semaine');
    }

    return result.data;
  }

  /**
   * Récupérer l'agenda d'un mois
   */
  static async getMonthSchedule(date: Date): Promise<{
    monthStart: string;
    monthEnd: string;
    appointments: Appointment[];
  }> {
    const dateStr = toLocalDateString(date);

    const response = await fetch(`${API_BASE_URL}/schedule/month/${dateStr}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<any> = await response.json();

    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la récupération de l\'agenda du mois');
    }

    return result.data;
  }

  /**
   * Créer un nouveau rendez-vous
   */
  static async createAppointment(appointmentData: AppointmentFormData): Promise<Appointment> {
    const response = await fetch(`${API_BASE_URL}/schedule/appointments`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: JSON.stringify({
        clientId: appointmentData.clientId,
        startTime: appointmentData.startTime.toISOString(),
        endTime: appointmentData.endTime.toISOString(),
        duration: appointmentData.duration,
        title: appointmentData.title,
        description: appointmentData.description,
        notes: appointmentData.notes
      }),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<Appointment> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la création du rendez-vous');
    }

    return result.data!;
  }

  /**
   * Mettre à jour un rendez-vous
   */
  static async updateAppointment(id: string, appointmentData: Partial<AppointmentFormData>): Promise<Appointment> {
    const updateData: any = {};
    
    if (appointmentData.startTime) updateData.startTime = appointmentData.startTime.toISOString();
    if (appointmentData.endTime) updateData.endTime = appointmentData.endTime.toISOString();
    if (appointmentData.duration) updateData.duration = appointmentData.duration;
    if (appointmentData.title) updateData.title = appointmentData.title;
    if (appointmentData.description) updateData.description = appointmentData.description;
    if (appointmentData.notes) updateData.notes = appointmentData.notes;
    if (appointmentData.status) updateData.status = appointmentData.status;

    const response = await fetch(`${API_BASE_URL}/schedule/appointments/${id}`, {
      method: 'PUT',
      headers: buildAuthHeaders(),
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<Appointment> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la mise à jour du rendez-vous');
    }

    return result.data!;
  }

  /**
   * Supprimer un rendez-vous
   */
  static async deleteAppointment(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/schedule/appointments/${id}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<void> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la suppression du rendez-vous');
    }
  }

  /**
   * Récupérer les créneaux disponibles
   */
  static async getAvailableSlots(
    startDate: Date,
    endDate: Date,
    duration?: number
  ): Promise<TimeSlot[]> {
    const queryParams = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    
    if (duration) {
      queryParams.append('duration', duration.toString());
    }

    const response = await fetch(`${API_BASE_URL}/schedule/available-slots?${queryParams.toString()}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<TimeSlot[]> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la récupération des créneaux disponibles');
    }

    return result.data!;
  }

  /**
   * Trouver le prochain créneau disponible
   */
  static async getNextAvailableSlot(
    duration: number = 60,
    afterDate?: Date
  ): Promise<TimeSlot | null> {
    const queryParams = new URLSearchParams({
      duration: duration.toString(),
    });
    
    if (afterDate) {
      queryParams.append('afterDate', afterDate.toISOString());
    }

    const response = await fetch(`${API_BASE_URL}/schedule/next-slot?${queryParams.toString()}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<TimeSlot | null> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors de la recherche du prochain créneau');
    }

    return result.data!;
  }

  /**
   * Obtenir les statistiques d'une journée
   */
  static async getDayStatistics(date: Date): Promise<DayStatistics> {
    const dateStr = toLocalDateString(date);
    
    const response = await fetch(`${API_BASE_URL}/schedule/statistics/${dateStr}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result: ApiResponse<DayStatistics> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur lors du calcul des statistiques');
    }

    return result.data!;
  }

  /**
   * Changer le statut d'un rendez-vous
   */
  static async updateAppointmentStatus(
    id: string, 
    status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show'
  ): Promise<Appointment> {
    return this.updateAppointment(id, { status });
  }
}

export default ScheduleApiService;